import { ApplicationRef, ViewContainerRef, ComponentRef, EmbeddedViewRef, Injector, Type, Injectable, OnDestroy, Optional, Inject } from '@angular/core';

import { Observable, tap, take, Subscription } from 'rxjs';


import { AbstractDialogComponent } from '../components/AbstractDialog/abstract-dialog.component';
import { DialogIoModel, DialogIoModelObservable, outputIncludesDialogCloseOperator } from '../models/dialog-io.model';
import { NavigationStart, Router } from '@angular/router';

@Injectable()
export class CoreDialogService implements OnDestroy {
    dialogComponentRefMap: Map<string, ComponentRef<AbstractDialogComponent>>;

    // list of modal keys we do not want to close when there is a navigation change
    doNotCloseOnPageChange: string[] = ['DistributionConfirmation', 'ManageLhpList'];

    routerSub: Subscription;
    public viewContainer: ViewContainerRef;
    private _appRef?: ApplicationRef;

    constructor(
        public injector: Injector,
        public router: Router,
        @Optional() @Inject('CoreDialogMap') private dialogMap: Map<string, Type<AbstractDialogComponent>>
    ) {
        this.dialogComponentRefMap = new Map();
        this.routerSub = this.router.events.subscribe(event => {
            if (event instanceof NavigationStart && this.hasOpenedDialogs()) {
                this.closeAllExcept();
            }
        });
    }

    /**
     * Appends the dialog to the DOM and registers it in the map
     * @param dialogType The component class of the dialog being opened/a string mapping to that component class in the provided map
     * @param io Inputs and outputs for the dialog
     * @param injector Custom injector override necessary in some cases to make sure it has everything it needs through dependency injection
     * @param overrideKey Overrides the component key so common dialogs have be single instance by default and multi-instance when necessary
     * @returns Key of the opened dialog.
     */
    public open(
        dialogType: Type<AbstractDialogComponent> | string,
        io?: DialogIoModel,
        injector?: Injector,
        overrideKey?: string
    ): string {
        // eslint-disable-next-line max-len
        // There are times that we want to be able to launch a dialog from a Common/Core component, but the dialog is not Common/Core, this is likely an indication
        // that we should not be referencing it there, but another option for this workaround is to provide an application level map of strings to components
        // so that we don't need to import the type, we can simply refer to the type by string instead, and provide a different map in each application
        let dialogKey: Type<AbstractDialogComponent>;
        if (typeof dialogType === 'string') {
            dialogKey = this.dialogMap.get(dialogType);
            if (!dialogKey) {
                throw new Error('Unmapped dialog type: ' + dialogType);
            }
        } else {
            dialogKey = dialogType;
        }
        const componentRef = this.appendDialogComponentToBody(dialogKey, injector);
        let key = componentRef.instance.key;

        if (overrideKey) {
            key = overrideKey;
            componentRef.instance.key = overrideKey;
        }

        if (this.dialogComponentRefMap.has(key)) {
            // destroy the original so this one can take it's place
            this.close(key);
        }

        this.dialogComponentRefMap.set(key, componentRef);

        if (io) {
            const instance = componentRef.instance;

            if (io.inputs) {
                for (const input in io.inputs) {
                    if (io.inputs.hasOwnProperty(input)) {
                        instance[input] = io.inputs[input];
                    }
                }
            }

            if (io.outputs) {
                for (const output in io.outputs) {
                    if (io.outputs.hasOwnProperty(output)) {
                        instance[output].subscribe(io.outputs[output]);
                    }
                }
            }
        }

        return key;
    }


    /**
     * The same as {@link CoreDialogService#open}, except the dialog component's dialogClose event emitter is treated as an observable,
     * and returned using either a passed in OperatorFunction on the io model, or a 'pass through' operator, as a piped operator after
     * the dialogClose event is emitted.
     *
     * Note that this method does not return the instance key of the created dialog.
     * @param dialogType The component class of the dialog being opened
     * @param io Inputs and outputs for the dialog
     * @param injector Custom injector override necessary in some cases to make sure it has everything it needs through dependency injection
     * @param overrideKey Overrides the component key so common dialogs have be single instance by default and multi-instance when necessary
     * @returns
     */
    public openWithObservable(
        dialogType: Type<AbstractDialogComponent> | string,
        io?: DialogIoModelObservable,
        injector?: Injector,
        overrideKey?: string
    ): Observable<any> {
        let dialogKey: Type<AbstractDialogComponent>;
        if (typeof dialogType === 'string') {
            dialogKey = this.dialogMap.get(dialogType);
            if (!dialogKey) {
                throw new Error('Unmapped dialog type: ' + dialogType);
            }
        } else {
            dialogKey = dialogType;
        }
        const componentRef = this.appendDialogComponentToBody(dialogKey, injector);
        let key = componentRef.instance.key;

        if (overrideKey) {
            key = overrideKey;
            componentRef.instance.key = overrideKey;
        }

        if (this.dialogComponentRefMap.has(key)) {
            // destroy the original so this one can take it's place
            this.close(key);
        }

        this.dialogComponentRefMap.set(key, componentRef);

        // If no dialogClose operation is specified, just pass through the original emitted value
        let operator = tap();
        if (io && outputIncludesDialogCloseOperator(io)) {
            operator = io.dialogCloseOperator ?? tap();
        }

        const obs = componentRef.instance.dialogClose.pipe(
            operator,
            take(1)
        );

        if (io) {
            const instance = componentRef.instance;

            if (io.inputs) {
                for (const input in io.inputs) {
                    if (io.inputs.hasOwnProperty(input)) {
                        instance[input] = io.inputs[input];
                    }
                }
            }

            if (io.outputs) {
                for (const output in io.outputs) {
                    if (io.outputs.hasOwnProperty(output) && output !== 'dialogCloseOperator') {
                        instance[output].subscribe(io.outputs[output]);
                    }
                }
            }
        }

        return obs;
    }

    /**
     * Removes the dialog from the DOM and removes it from the map
     */
    public close(key: string) {
        const componentRef = this.dialogComponentRefMap.get(key);
        if (componentRef) {
            this.removeDialogComponentFromBody(componentRef);
            this.dialogComponentRefMap.delete(key);
        }
    }

    closeViaComponent(key: string, result?: any) {
        const ref = this.dialogComponentRefMap.get(key);
        if (ref?.instance && 'closeDialog' in ref.instance) {
            (ref.instance as AbstractDialogComponent).closeDialog(result);
        } else {
            this.close(key);
        }
    }

    /**
     * Removes all dialogs from the DOM
     */
    public closeAll() {
        this.dialogComponentRefMap.forEach((value, key) => {
            if (value) {
                this.removeDialogComponentFromBody(value);
                this.dialogComponentRefMap.delete(key);
            }
        });
    }

    /**
     * Removes all dialogs from the DOM, except the ones passed in the doNotCloseOnPageChange array
     */
    public closeAllExcept() {
        this.dialogComponentRefMap.forEach((value, key) => {
            if (value && !this.doNotCloseOnPageChange.includes(key)) {
                this.removeDialogComponentFromBody(value);
                this.dialogComponentRefMap.delete(key);
            }
        });
    }

    /**
     * Removes all dialogs from the DOM, except the ones passed in the doNotCloseOnPageChange array
     */
    public closeAllExceptKeys(keys: string[]) {
        this.dialogComponentRefMap.forEach((value, key) => {
            if (value && !keys.includes(key)) {
                this.removeDialogComponentFromBody(value);
                this.dialogComponentRefMap.delete(key);
            }
        });
    }

    /**
     * returns a boolean that indicates if there are any opened dialogs
     */
    public hasOpenedDialogs(): boolean {
        return this.dialogComponentRefMap.size > 0;
    }

    public getLastDialog(): ComponentRef<AbstractDialogComponent> {
        return Array.from(this.dialogComponentRefMap.values()).pop();
    }

    public getDialog(key: string): ComponentRef<AbstractDialogComponent> {
        return this.dialogComponentRefMap.get(key);
    }

    /**
     * Adds a component of the specified type to the DOM
     */
    private appendDialogComponentToBody(dialogType: Type<AbstractDialogComponent>, injector: Injector = this.injector): ComponentRef<AbstractDialogComponent> {
        const componentRef = this.viewContainer.createComponent(dialogType, {injector: injector});
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        document.body.appendChild(domElem);

        return componentRef;
    }

    private get appRef(): ApplicationRef {
        if (!this._appRef) {
            this._appRef = this.injector.get(ApplicationRef);
        }
        return this._appRef;
    }

    /**
     * Removes the specified component from the DOM
     */
    private removeDialogComponentFromBody(componentRef: ComponentRef<AbstractDialogComponent>): void {
        this.appRef.detachView(componentRef.hostView);
        componentRef.location.nativeElement.remove();
        componentRef.destroy();
    }

    public getIoConfigBasedOnMousePosition(mousePositionX: number, mousePositionY: number, popupWidth: number, popupHeight): DialogIoModel {
        let positionX = 0;
        let positionY = 0;
        const offsetSmall = 10;

        if ((mousePositionX + popupWidth) > window.innerWidth) {
            positionX = positionX = Math.min(
                window.innerWidth - popupWidth - offsetSmall,
                mousePositionX + ((Math.abs(popupWidth) / 2) || 100)
            );
        } else {
            positionX = Math.abs(mousePositionX - (popupWidth / 2)) || 100;
        }

        if (window.innerHeight < (popupHeight + mousePositionY)) {
            positionY = window.innerHeight - popupHeight - offsetSmall;
        } else {
            positionY = mousePositionY + offsetSmall;
        }

        const ioModel: DialogIoModel = {
            inputs: {
                positionX: positionX,
                positionY: positionY,
            }
        };

        return ioModel;
    }

    /**
     * Positions a popup dialog in the middle of the viewport, for popups that are not anchored to what was clicked
     */
    public getIoConfigCentered(popupWidth: number, popupHeight: number): DialogIoModel {
        const offsetSmall = 10;

        return {
            inputs: {
                positionX: Math.max(offsetSmall, (window.innerWidth - popupWidth) / 2),
                positionY: Math.max(offsetSmall, (window.innerHeight - popupHeight) / 2),
            }
        };
    }

    ngOnDestroy(): void {
        this.routerSub.unsubscribe();
    }
}
